// src/pages/common-components/AIChat/AIChatWidget.tsx
// Floating AI chat assistant, visible on every client page. Calls the
// `chatWithAI` Cloud Function (Google Gemini) — see
// functions/functions/functions/src/chat.ts.
import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../Firebase/Config';
import { useCompany } from '../../Contexts/CompanyContext';

interface ChatMessage {
  role: 'user' | 'model';
  text: string;
}

const chatWithAI = httpsCallable<
  { message: string; history: ChatMessage[]; audience: 'client'; companyName: string },
  { reply: string }
>(functions, 'chatWithAI');

const AIChatWidget: React.FC = () => {
  const { companyProfile } = useCompany();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    setError('');

    try {
      const result = await chatWithAI({
        message: text,
        history: messages,
        audience: 'client',
        companyName: companyProfile?.companyName || 'our dealership',
      });
      setMessages([...nextMessages, { role: 'model', text: result.data.reply }]);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ai-chat-widget">
      {open && (
        <div
          style={{
            width: 340,
            maxWidth: 'calc(100vw - 48px)',
            height: 440,
            maxHeight: 'calc(100vh - 140px)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              background: '#222',
              color: '#fff',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              Ask {companyProfile?.companyName || 'us'} anything
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            >
              &times;
            </button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 8px' }}>
            {messages.length === 0 && (
              <div style={{ fontSize: 13, color: '#888', textAlign: 'center', marginTop: 24 }}>
                Ask about financing, test drives, or how buying a car here works.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '9px 12px',
                    borderRadius: 12,
                    fontSize: 13.5,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                    background: m.role === 'user' ? '#717fe0' : '#f1f2f6',
                    color: m.role === 'user' ? '#fff' : '#222',
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div style={{ fontSize: 12.5, color: '#999', padding: '2px 4px' }}>Typing…</div>
            )}
            {error && (
              <div style={{ fontSize: 12.5, color: '#c62828', padding: '2px 4px' }}>{error}</div>
            )}
          </div>

          <form onSubmit={handleSend} style={{ display: 'flex', borderTop: '1px solid #eee', padding: 8, gap: 8 }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message…"
              style={{
                flex: 1,
                border: '1px solid #ddd',
                borderRadius: 20,
                padding: '9px 14px',
                fontSize: 13.5,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              style={{
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                background: '#717fe0',
                color: '#fff',
                fontSize: 15,
                cursor: sending || !input.trim() ? 'default' : 'pointer',
                opacity: sending || !input.trim() ? 0.6 : 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Send"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        className="ai-chat-toggle-btn"
        style={{
          borderRadius: '50%',
          border: 'none',
          background: '#717fe0',
          color: '#fff',
          fontSize: 24,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 'auto',
        }}
      >
        {open ? (
          <span style={{ lineHeight: 1 }}>&times;</span>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default AIChatWidget;
